import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PlexataIcon } from '../components/PlexataLogo';

const AuthCallback = () => {
  const { processGoogleCallback, setLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for token in query params (new Google OAuth flow)
      const token = searchParams.get('token');
      const error = searchParams.get('error');
      const redirectPath = searchParams.get('redirect') || '/dashboard';
      
      if (error) {
        console.error('Auth error:', error);
        navigate('/login', { replace: true, state: { error: 'Authentication failed. Please try again.' } });
        return;
      }
      
      if (token) {
        try {
          setLoading(true);
          processGoogleCallback(token);
          // Redirect to the intended destination (e.g., /store after checkout OAuth)
          navigate(redirectPath, { replace: true });
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true, state: { error: 'Authentication failed. Please try again.' } });
        }
        return;
      }
      
      // Fallback: check for session_id in hash (legacy Emergent flow)
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        navigate('/login', { replace: true, state: { error: 'Please use the updated login flow.' } });
        return;
      }
      
      // No valid auth data found
      navigate('/login', { replace: true });
    };

    processAuth();
  }, [searchParams, location.hash, navigate, processGoogleCallback, setLoading]);

  // Show loading while processing
  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <PlexataIcon size={48} className="mx-auto mb-4 animate-pulse" />
        <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-500 text-sm">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
